import React, { useState, useEffect, useCallback } from 'react';
import Node from '../assets/icons/Node';
import ReactJS from '../assets/icons/React';
import Android from '../assets/icons/Android';
import Apple from '../assets/icons/Apple';
import AWS from '../assets/icons/AWS';

import p1_1 from '../assets/projects/project1/project1.webp';

import p2_1 from '../assets/projects/project2/idcapp1.png';
import p2_2 from '../assets/projects/project2/idcapp2.png';
import p2_3 from '../assets/projects/project2/idcapp3.png';
import p2_4 from '../assets/projects/project2/idcapp4.png';
import p2_5 from '../assets/projects/project2/idcapp5.png';
import p2_6 from '../assets/projects/project2/idcapp6.png';
import p2_7 from '../assets/projects/project2/idcapp7.png';
import p2_8 from '../assets/projects/project2/idcapp8.png';
import p2_9 from '../assets/projects/project2/idcapp9.png';

import p3_1 from '../assets/projects/project3/project3_1.png';
import p3_2 from '../assets/projects/project3/project3_2.png';
import p3_3 from '../assets/projects/project3/project3_3.png';
import p3_4 from '../assets/projects/project3/project3_4.png';
import p3_5 from '../assets/projects/project3/project3_5.png';

import { useT } from '../context/LanguageContext';

// ─── Tags (display names are proper nouns, no translation needed) ─────────────
const TAGS = {
  REACT:   { name: 'React',        class: 'bg-zinc-700 text-yellow-200 border border-yellow-200/20', icon: ReactJS },
  REACTN:  { name: 'React Native', class: 'bg-zinc-700 text-yellow-200 border border-yellow-200/20', icon: ReactJS },
  NODE:    { name: 'Node JS',      class: 'bg-zinc-700 text-white border border-white/10',           icon: Node    },
  ANDROID: { name: 'Android',      class: 'bg-zinc-700 text-green-400 border border-green-400/20',   icon: Android },
  APPLE:   { name: 'iOS',          class: 'bg-zinc-700 text-white border border-white/10',           icon: Apple   },
  AWS:     { name: 'AWS',          class: 'bg-zinc-700 text-orange-400 border border-orange-400/20', icon: AWS     },
};

const STATUS_STYLES = {
  inProgress: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
  published:  'text-green-400  bg-green-400/10  border-green-400/30',
  completed:  'text-blue-400   bg-blue-400/10   border-blue-400/30',
};

// Static project data — images and links only (text comes from translations)
const PROJECTS_STATIC = [
  {
    images: [p1_1],
    tags: [TAGS.REACT, TAGS.NODE, TAGS.AWS, TAGS.REACTN],
    links: [],
  },
  {
    images: [p2_1, p2_2, p2_3, p2_4, p2_5, p2_6, p2_7, p2_8, p2_9],
    tags: [TAGS.REACTN, TAGS.NODE, TAGS.ANDROID, TAGS.APPLE],
    links: [
      { icon: Android, url: 'https://play.google.com/store/apps/details?id=mx.idconline.idcapp&hl=es_MX', label: 'Google Play' },
      { icon: Apple,   url: 'https://apps.apple.com/mx/app/app-idc/id6464002645',                         label: 'App Store'   },
    ],
  },
  {
    images: [p3_1, p3_2, p3_3, p3_4, p3_5],
    tags: [TAGS.REACT, TAGS.NODE],
    links: [],
  },
];

// ─── Fullscreen viewer ────────────────────────────────────────────────────────
function FullscreenViewer({ images, startIndex, onClose }) {
  const [current, setCurrent] = useState(startIndex);

  const prev = useCallback(() => setCurrent(c => (c === 0 ? images.length - 1 : c - 1)), [images.length]);
  const next = useCallback(() => setCurrent(c => (c === images.length - 1 ? 0 : c + 1)), [images.length]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prev, next, onClose]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.96)' }}
      onClick={onClose}
    >
      <img
        src={images[current]}
        alt={`Screenshot ${current + 1}`}
        className="max-w-[92vw] max-h-[88vh] object-contain select-none rounded-lg"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
      />

      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center
                   rounded-full bg-white/10 text-white hover:bg-white/20 focus:outline-none"
        aria-label="Close"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center
                       rounded-full bg-white/10 text-white hover:bg-white/20 focus:outline-none"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center
                       rounded-full bg-white/10 text-white hover:bg-white/20 focus:outline-none"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <span className="absolute bottom-5 left-1/2 -translate-x-1/2 text-sm text-white/60 bg-black/40 px-3 py-1 rounded-full">
            {current + 1} / {images.length}
          </span>
        </>
      )}
    </div>
  );
}

// ─── Modal carousel ───────────────────────────────────────────────────────────
function ModalCarousel({ images, title, onOpenFullscreen, clickToExpand }) {
  const [current, setCurrent] = useState(0);

  const prev = useCallback(() => setCurrent(c => (c === 0 ? images.length - 1 : c - 1)), [images.length]);
  const next = useCallback(() => setCurrent(c => (c === images.length - 1 ? 0 : c + 1)), [images.length]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="relative w-full rounded-xl overflow-hidden bg-zinc-950 group">
        <img
          src={images[current]}
          alt={`${title} screenshot ${current + 1}`}
          className="w-full object-cover cursor-zoom-in"
          style={{ maxHeight: 'min(340px, 42vh)' }}
          loading="lazy"
          onClick={() => onOpenFullscreen(current)}
        />

        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="flex items-center gap-1.5 text-xs text-white bg-black/50 px-3 py-1.5 rounded-full
                           opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
            {clickToExpand}
          </span>
        </div>

        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center
                         rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100
                         hover:bg-black/80 focus:outline-none"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center
                         rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100
                         hover:bg-black/80 focus:outline-none"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <span className="absolute top-2 right-2 text-xs bg-black/60 text-white px-2 py-0.5 rounded-full pointer-events-none">
              {current + 1} / {images.length}
            </span>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`shrink-0 w-14 h-10 rounded-lg overflow-hidden border-2 focus:outline-none
                ${i === current ? 'border-yellow-300 opacity-100' : 'border-transparent opacity-45 hover:opacity-75'}`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Project modal ────────────────────────────────────────────────────────────
function ProjectModal({ project, onClose, labels }) {
  const [fullscreenIndex, setFullscreenIndex] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && fullscreenIndex === null) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, fullscreenIndex]);

  if (!project) return null;
  const statusLabel = labels.status[project.statusKey];
  const statusStyle = STATUS_STYLES[project.statusKey];

  return (
    <>
      <div
        className="fixed inset-0 flex items-center justify-center p-3 sm:p-5 lg:p-8"
        style={{ zIndex: 1200, backgroundColor: 'rgba(0,0,0,0.80)', backdropFilter: 'blur(6px)' }}
        onClick={onClose}
      >
        <div
          className="relative w-full bg-black rounded-2xl shadow-2xl border border-white/10
                     flex flex-col md:flex-row overflow-hidden"
          style={{ maxWidth: '1100px', maxHeight: '92vh' }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          {/* Carousel */}
          <div className="md:w-[55%] flex-shrink-0 bg-zinc-950 p-4 flex flex-col justify-center overflow-y-auto">
            <ModalCarousel
              images={project.images}
              title={project.title}
              onOpenFullscreen={setFullscreenIndex}
              clickToExpand={labels.clickToExpand}
            />
          </div>

          {/* Info */}
          <div className="flex-1 overflow-y-auto p-5 sm:p-6 flex flex-col gap-4 min-w-0">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center
                         rounded-full bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700
                         focus:outline-none"
              aria-label={labels.close}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="pr-8">
              <h2 className="text-xl font-bold text-yellow-200 leading-snug">{project.title}</h2>
              <span className={`inline-block mt-2 text-xs font-medium px-2.5 py-1 rounded-full border ${statusStyle}`}>
                {statusLabel}
              </span>
            </div>

            <p className="text-sm sm:text-base text-zinc-400 leading-relaxed">{project.description}</p>

            <div>
              <p className="text-xs text-zinc-600 uppercase tracking-wider mb-2">{labels.stack}</p>
              <ul className="flex flex-wrap gap-2">
                {project.tags.map((tag, i) => (
                  <li key={i}>
                    <span className={`flex items-center gap-1.5 rounded-full text-xs py-1 px-3 ${tag.class}`}>
                      <tag.icon className="w-3.5 h-3.5" />
                      {tag.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {project.links.length > 0 && (
              <div className="mt-auto pt-3 border-t border-white/5">
                <p className="text-xs text-zinc-600 uppercase tracking-wider mb-2">{labels.availableOn}</p>
                <div className="flex flex-wrap gap-2">
                  {project.links.map((link, i) => (
                    <a
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white
                                 bg-zinc-800 hover:bg-zinc-700 px-4 py-2 rounded-xl
                                 border border-white/5 hover:border-white/20 group/link"
                    >
                      <link.icon className="w-4 h-4 group-hover/link:text-yellow-200" />
                      <span>{link.label}</span>
                      <svg className="w-3 h-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {fullscreenIndex !== null && (
        <FullscreenViewer
          images={project.images}
          startIndex={fullscreenIndex}
          onClose={() => setFullscreenIndex(null)}
        />
      )}
    </>
  );
}

// ─── Project card ─────────────────────────────────────────────────────────────
function ProjectCard({ project, onClick, labels }) {
  const statusLabel = labels.status[project.statusKey];
  const statusStyle = STATUS_STYLES[project.statusKey];

  return (
    <article
      className="group flex flex-col bg-black rounded-xl overflow-hidden shadow-xl cursor-pointer
                 border border-white/5 hover:border-yellow-200/25
                 hover:-translate-y-1 hover:shadow-2xl hover:shadow-yellow-200/5"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="relative aspect-video overflow-hidden bg-zinc-900">
        <img
          src={project.images[0]}
          alt={project.title}
          className="w-full h-full object-cover group-hover:scale-105"
          loading="lazy"
        />

        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 flex items-center justify-center">
          <span className="flex items-center gap-2 text-white text-sm font-medium
                           bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full
                           opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            {labels.viewProject}
          </span>
        </div>

        {project.images.length > 1 && (
          <span className="absolute top-3 left-3 text-xs bg-black/60 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {project.images.length}
          </span>
        )}
      </div>

      <div className="p-4 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-yellow-200 leading-snug truncate">{project.title}</h3>
          <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full border ${statusStyle}`}>
            {statusLabel}
          </span>
        </div>
        <p className="text-xs text-zinc-500 line-clamp-1">{project.shortDesc}</p>
        <div className="flex items-center gap-1.5 pt-1">
          {project.tags.map((tag, i) => (
            <span key={i} title={tag.name} className={`flex items-center rounded-full p-1.5 ${tag.class}`}>
              <tag.icon className="w-3 h-3" />
            </span>
          ))}
          <span className="ml-auto text-xs text-zinc-600 group-hover:text-zinc-400">
            {labels.clickToView}
          </span>
        </div>
      </div>
    </article>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
function Projects() {
  const { t } = useT();
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Merge static data (images, links) with translated text
  const projects = PROJECTS_STATIC.map((staticData, i) => ({
    ...staticData,
    ...t.projects.items[i],
  }));

  const selectedProject = selectedIndex !== null ? projects[selectedIndex] : null;

  return (
    <>
      <div
        className="w-full bg-cover bg-left-top"
        style={{ backgroundImage: 'url(https://themes.muffingroup.com/be/digital/wp-content/uploads/2015/07/home_digital_pattern_1.png)' }}
      >
        <div className="bg-white w-full px-[6%] sm:px-[8%] lg:px-[10%] py-16">

          <div className="mb-10 text-center">
            <p className="text-3xl font-bold sm:text-4xl tracking-tight">{t.projects.sectionLabel}</p>
            <p className="mt-3 text-base sm:text-lg font-light text-gray-500 max-w-lg mx-auto">
              {t.projects.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((project, index) => (
              <ProjectCard
                key={index}
                project={project}
                onClick={() => setSelectedIndex(index)}
                labels={t.projects}
              />
            ))}
          </div>

        </div>
      </div>

      {selectedProject && (
        <ProjectModal
          project={selectedProject}
          onClose={() => setSelectedIndex(null)}
          labels={t.projects}
        />
      )}
    </>
  );
}

export default Projects;
